"use client";

import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import styles from "./CustomCard.module.scss";
import Text from "@components/atoms/Text";
import Button from "@components/atoms/Button";
import PriceBreakoutSection from "../PriceBreakoutSection";
import DeliveryCard from "../DeliveryCard";
import FeatureTag from "@components/atoms/FeatureTag";
import assetLoader from "@utils/internalImageLoder";
import {
  formatCurrencyINR,
  formatPrice,
  maskAmount,
} from "@utils/numberFormatter";
import { IVariantDetailsPrice } from "./CustomCard.types";
import { useRouter } from "next/navigation";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import {
  type NormalizedCustomCardPriceBreakup,
  normalizeCustomCardPriceBreakup,
  type V2PriceBreakupResponse,
} from "@helpers/customCardHelper";
import { PageType } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { LoginSdkConfig } from "@enums/LoginSdkConfig";
import { handleLoginFlow } from "@utils/loginFlowHandler";
import CustomLink from "@components/atoms/Link";
import { fetchCachedPriceBreakupV2 } from "@services/shortlistPricingService";

interface CustomCardV2Props {
  data: IVariantDetailsPrice;
  isExpanded: boolean;
  onToggle: () => void;
  isLoggedIn: boolean;
  city: any;
  brandName: string;
  modelName: string;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  savingBalance?: any;
  initialPriceBreakup?: V2PriceBreakupResponse | null;
}

function CustomCardV2({
  data,
  isExpanded,
  onToggle,
  isLoggedIn,
  city,
  brandName,
  modelName,
  isSellable,
  pageType,
  carAvailability,
  savingBalance,
  initialPriceBreakup,
}: CustomCardV2Props) {
  const { name: selectedCityName } = getSelectedCityClient();
  const router = useRouter();
  const [rawPriceBreakup, setRawPriceBreakup] =
    useState<V2PriceBreakupResponse | null>(initialPriceBreakup || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchBreakup = async () => {
      if (!isExpanded || rawPriceBreakup) {
        return;
      }

      try {
        setLoading(true);
        const result = await fetchCachedPriceBreakupV2({
          variantSlug: data.slug,
          cityName: city?.name || "",
        });

        if (!isMounted) {
          return;
        }

        if (result?.success && result.data) {
          setRawPriceBreakup(result.data);
          return;
        }

        console.error("Failed to fetch price breakup:", result?.error);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch price breakup", error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchBreakup();

    return () => {
      isMounted = false;
    };
  }, [city?.name, data.slug, isExpanded, rawPriceBreakup]);

  useEffect(() => {
    setRawPriceBreakup(initialPriceBreakup || null);
  }, [initialPriceBreakup]);

  const normalizedPriceBreakup = useMemo<NormalizedCustomCardPriceBreakup | null>(
    () =>
      normalizeCustomCardPriceBreakup({
        priceBreakupData: rawPriceBreakup,
        cityName: city?.name || selectedCityName || "",
        isLoggedIn,
      }),
    [city?.name, isLoggedIn, rawPriceBreakup, selectedCityName]
  );

  const handleExploreClick = useCallback(async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || "",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_variants_see_details",
        section: "Variant",
        variant_name: data?.variantName || "",
        car_availability: carAvailability || "",
        vehicle_make: brandName || "",
        vehicle_model: modelName || "",
        city_name: selectedCityName || "",
      },
    });

    router.push(`/cars/${data.slug}`);
  }, [
    brandName,
    carAvailability,
    data?.slug,
    data?.variantName,
    modelName,
    pageType,
    router,
    selectedCityName,
  ]);

  const handleLoginToSave = useCallback(async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || "",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_login_to_save",
        section: "Variant",
        variant_name: data?.variantName || "",
        car_availability: carAvailability || "",
        vehicle_make: brandName || "",
        vehicle_model: modelName || "",
        city_name: selectedCityName || "",
      },
    });

    const mmvDetails = {
      make: brandName,
      model: modelName,
      variant: data?.variantName || "",
      color: "",
    };

    if (typeof window !== "undefined") {
      sessionStorage.setItem("loginType", "Reveal price");
      sessionStorage.setItem("flowType", "New Car");
      localStorage.setItem("MMV", JSON.stringify(mmvDetails));
    }

    const loginUrl = getLoginUrl(
      addCaptureIntentIfNeeded(
        window.location.href,
        mmvDetails,
        "Reveal price",
        pageType,
        "New Car"
      )
    );

    await handleLoginFlow(loginUrl, LoginSdkConfig.AD_PRICE);
  }, [
    brandName,
    carAvailability,
    data?.variantName,
    modelName,
    pageType,
    selectedCityName,
  ]);

  return (
    <div className={styles.rootContainer}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTopRow}>
          <CustomLink
            href={`/cars/${data.slug}`}
            className={styles.variantTitleLink}
          >
            <Text level="p" className={styles.variantTitle}>
              {data.variantName}
            </Text>
          </CustomLink>

          <div className={styles.priceGroup}>
            <Text
              level="p"
              className={
                isSellable && isLoggedIn
                  ? styles.strikePrice
                  : styles.currentPrice
              }
            >
              {`${formatPrice(data.onRoadPrice)}`}
            </Text>
            {isSellable && isLoggedIn && (
              <Text level="p" className={styles.currentPrice}>
                {`${formatPrice(data.adspPrice)}`}
              </Text>
            )}
          </div>
        </div>

        <div className={styles.cardSubInfoWrapper}>
          <div className={styles.cardSubInfoRow}>
            <Text level="p" className={styles.fuelText}>
              {`${data.fuelType} • ${data.transmission}`}
            </Text>
            {isSellable && (
              <Text level="p" className={styles.savingsText}>
                {`Save up to ${
                  isLoggedIn
                    ? formatCurrencyINR(data.ackoDriveSavings ?? 0)
                    : (() => {
                        const savings = data.ackoDriveSavings;
                        if (!savings) return "₹0";
                        if (typeof savings === "number") {
                          return maskAmount(savings);
                        }
                        const strValue = String(savings);
                        return strValue.startsWith("₹")
                          ? strValue
                          : `₹${strValue}`;
                      })()
                }`}
              </Text>
            )}
          </div>
        </div>

        <div className={styles.featuresWrapper}>
          <div className={styles.featuresContainer}>
            {data.featureItemDtos.map((feature) => (
              <FeatureTag
                key={feature.name}
                feature={`${feature.name} ${
                  feature.value ? ` - ${feature.value}` : ""
                }`}
              />
            ))}
          </div>
          <Button className={styles.showMoreButton} onClick={onToggle}>
            <Image
              src={`${assetLoader()}/icons/showmore.png`}
              loading="lazy"
              alt="Toggle"
              width={32}
              height={32}
              className={`${styles.icon} ${
                isExpanded ? styles.iconRotate : ""
              }`}
            />
          </Button>
        </div>
      </div>

      <div
        className={`${styles.expandableWrapper} ${
          isExpanded ? styles.expanded : ""
        }`}
      >
        {loading ? (
          <div className={styles.loader}>Loading price breakup...</div>
        ) : (
          normalizedPriceBreakup && (
            <div className={styles.wrapper}>
              <div className={styles.container}>
                <PriceBreakoutSection
                  charges={normalizedPriceBreakup.charges}
                  discounts={[]}
                  savingsRows={normalizedPriceBreakup.savingsRows}
                  summaryRows={normalizedPriceBreakup.summaryRows}
                  finalPrice={normalizedPriceBreakup.finalPrice}
                  isSellable={isSellable}
                  savingBalance={savingBalance}
                />

                <div className={styles.rightBox}>
                  {isSellable && (
                    <>
                      <DeliveryCard
                        expressDelivery={normalizedPriceBreakup.isExpressDelivery}
                        deliveryDate={normalizedPriceBreakup.deliveryDate}
                        isLoggedIn={isLoggedIn}
                        pageType={pageType}
                        brandName={brandName}
                        modelName={modelName}
                        variantName={data?.variantName}
                        carAvailability={carAvailability}
                      />
                      <div className={styles.buttonWrapper}>
                        <Button
                          className={styles.buttonOutline}
                          onClick={handleExploreClick}
                        >
                          {`Explore ${data.variantName}`}
                        </Button>
                        {!isLoggedIn && (
                          <Button
                            className={styles.buttonPrimary}
                            onClick={handleLoginToSave}
                          >
                            Check ACKO Drive price
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                  {!isSellable && (
                    <div className={styles.nonSellableButtonWrapper}>
                      <Button
                        className={styles.buttonOutline}
                        onClick={handleExploreClick}
                      >
                        {`Explore ${data.variantName}`}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

const areFeatureItemsEqual = (
  prevFeatures: IVariantDetailsPrice["featureItemDtos"],
  nextFeatures: IVariantDetailsPrice["featureItemDtos"]
) =>
  prevFeatures.length === nextFeatures.length &&
  prevFeatures.every((feature, index) => {
    const nextFeature = nextFeatures[index];
    return (
      feature.name === nextFeature?.name &&
      feature.description === nextFeature?.description &&
      feature.value === nextFeature?.value
    );
  });

const areCardPropsEqual = (
  prevProps: Readonly<CustomCardV2Props>,
  nextProps: Readonly<CustomCardV2Props>
) => {
  const prevData = prevProps.data;
  const nextData = nextProps.data;

  return (
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.isLoggedIn === nextProps.isLoggedIn &&
    prevProps.isSellable === nextProps.isSellable &&
    prevProps.brandName === nextProps.brandName &&
    prevProps.modelName === nextProps.modelName &&
    prevProps.pageType === nextProps.pageType &&
    prevProps.carAvailability === nextProps.carAvailability &&
    prevProps.savingBalance === nextProps.savingBalance &&
    prevProps.initialPriceBreakup === nextProps.initialPriceBreakup &&
    prevProps.city?.name === nextProps.city?.name &&
    prevData.variantName === nextData.variantName &&
    prevData.slug === nextData.slug &&
    prevData.fuelType === nextData.fuelType &&
    prevData.transmission === nextData.transmission &&
    prevData.onRoadPrice === nextData.onRoadPrice &&
    prevData.adspPrice === nextData.adspPrice &&
    prevData.ackoDriveSavings === nextData.ackoDriveSavings &&
    prevData.status === nextData.status &&
    areFeatureItemsEqual(prevData.featureItemDtos, nextData.featureItemDtos)
  );
};

export default memo(CustomCardV2, areCardPropsEqual);
